/**
 * WholesalerDetailPage
 * Phase 16: Detailed view for individual wholesaler account management
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Phone,
  MapPin,
  Building2,
  CreditCard,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  Calendar,
  User,
  History,
  Plus,
  RefreshCw,
  Eye,
  EyeOff,
  Ban,
  CheckCircle,
  XCircle,
  FileText,
  ArrowDownCircle
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import {
  getWholesalerById,
  getTransactions,
  getWholesalerAlerts,
  processPayment,
  processCreditPurchase,
  createAdjustment,
  updateCreditLimit,
  updateStatus,
  toggleVisibility,
  downgradeWholesaler
} from '../api/wholesalers';
import LoadingSpinner from '../components/common/LoadingSpinner';

const WholesalerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin =
    user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin';

  // State
  const [wholesaler, setWholesaler] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCreditLimitModal, setShowCreditLimitModal] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);

  // Form states
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentMethod: 'cash',
    paymentReference: '',
    notes: ''
  });
  const [creditLimitForm, setCreditLimitForm] = useState({
    creditLimit: '',
    notes: ''
  });
  const [adjustmentForm, setAdjustmentForm] = useState({
    amount: '',
    reason: ''
  });
  const [statusForm, setStatusForm] = useState({
    status: '',
    reason: ''
  });
  const [downgradeForm, setDowngradeForm] = useState({
    reason: '',
    targetType: 'retail'
  });

  const [processing, setProcessing] = useState(false);

  // Fetch data
  useEffect(() => {
    fetchWholesalerData();
  }, [id]);

  const fetchWholesalerData = async () => {
    try {
      setLoading(true);
      const [wholesalerRes, transactionsRes, alertsRes] = await Promise.all([
        getWholesalerById(id),
        getTransactions(id, { limit: 50 }),
        isAdmin ? getWholesalerAlerts(id, { limit: 10 }) : Promise.resolve({ data: [] })
      ]);

      setWholesaler(wholesalerRes.data);
      setTransactions(transactionsRes.data || []);
      setAlerts(alertsRes.data || []);
    } catch (error) {
      console.error('Error fetching wholesaler data:', error);
      toast.error('Failed to load wholesaler data');
    } finally {
      setLoading(false);
    }
  };

  // Handle payment
  const handlePayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setProcessing(true);
      await processPayment(id, {
        amount: parseFloat(paymentForm.amount),
        paymentMethod: paymentForm.paymentMethod,
        paymentReference: paymentForm.paymentReference,
        notes: paymentForm.notes
      });
      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      setPaymentForm({ amount: '', paymentMethod: 'cash', paymentReference: '', notes: '' });
      fetchWholesalerData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };

  // Handle credit limit update
  const handleCreditLimitUpdate = async (e) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await updateCreditLimit(id, parseFloat(creditLimitForm.creditLimit), creditLimitForm.notes);
      toast.success('Credit limit updated successfully');
      setShowCreditLimitModal(false);
      setCreditLimitForm({ creditLimit: '', notes: '' });
      fetchWholesalerData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update credit limit');
    } finally {
      setProcessing(false);
    }
  };

  // Handle adjustment
  const handleAdjustment = async (e) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await createAdjustment(id, parseFloat(adjustmentForm.amount), adjustmentForm.reason);
      toast.success('Adjustment created successfully');
      setShowAdjustmentModal(false);
      setAdjustmentForm({ amount: '', reason: '' });
      fetchWholesalerData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create adjustment');
    } finally {
      setProcessing(false);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await updateStatus(id, statusForm.status, statusForm.reason);
      toast.success('Status updated successfully');
      setShowStatusModal(false);
      setStatusForm({ status: '', reason: '' });
      fetchWholesalerData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setProcessing(false);
    }
  };

  // Handle downgrade/convert
  const handleDowngrade = async (e) => {
    e.preventDefault();
    if (!downgradeForm.reason) {
      toast.error('Reason is required');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to convert this account to ${downgradeForm.targetType}?`
      )
    ) {
      try {
        setProcessing(true);
        // Call the API with targetType
        await downgradeWholesaler(id, downgradeForm.reason, downgradeForm.targetType);
        toast.success(`Account converted to ${downgradeForm.targetType} successfully`);
        // Redirect to wholesalers list or customers list (since they are no longer a wholesaler)
        navigate('/wholesalers');
      } catch (error) {
        console.error('Downgrade error:', error);
        toast.error(error.response?.data?.message || 'Failed to convert account');
      } finally {
        setProcessing(false);
      }
    }
  };

  // Toggle visibility
  const handleToggleVisibility = async () => {
    try {
      await toggleVisibility(id, !wholesaler.staff_can_view_balance);
      toast.success('Visibility settings updated');
      fetchWholesalerData();
    } catch (error) {
      toast.error('Failed to update visibility settings');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      case 'blocked':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get transaction type icon
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'credit_purchase':
        return <TrendingUp className="w-5 h-5 text-red-500" />;
      case 'payment':
        return <DollarSign className="w-5 h-5 text-green-500" />;
      case 'adjustment':
        return <Edit className="w-5 h-5 text-blue-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!wholesaler) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          Wholesaler not found
        </div>
      </div>
    );
  }

  const utilization = parseFloat(wholesaler.utilization_percentage) || 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/wholesalers')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-primary-black flex items-center gap-2">
              <Building2 className="w-7 h-7 text-accent-gold" />
              {wholesaler.customer_name}
            </h1>
            <p className="text-primary-slate mt-1">
              {wholesaler.customer_phone}
              {wholesaler.business_name && ` • ${wholesaler.business_name}`}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchWholesalerData}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-lightGray text-primary-charcoal rounded-lg hover:bg-gray-200 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          {isAdmin && wholesaler.status === 'active' && (
            <button
              onClick={() => setShowPaymentModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black rounded-lg hover:bg-accent-goldDark transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              Record Payment
            </button>
          )}
        </div>
      </div>

      {/* Status Badge */}
      <div className="mb-6 flex items-center gap-4">
        <span
          className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(wholesaler.status)}`}
        >
          {wholesaler.status}
        </span>
        {wholesaler.active_alerts_count > 0 && (
          <span className="inline-flex items-center px-3 py-1 text-sm font-medium bg-red-100 text-red-800 rounded-full">
            <AlertTriangle className="w-4 h-4 mr-1" />
            {wholesaler.active_alerts_count} Active Alert
            {wholesaler.active_alerts_count > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-primary-slate">Credit Limit</p>
              <p className="text-2xl font-bold text-primary-black">
                {formatCurrency(wholesaler.credit_limit)}
              </p>
            </div>
            <div className="w-12 h-12 bg-accent-gold/10 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-accent-gold" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-primary-slate">Outstanding Balance</p>
              <p className="text-2xl font-bold text-orange-600">
                {formatCurrency(wholesaler.credit_balance)}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-primary-slate">Available Credit</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(wholesaler.available_credit)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-primary-slate">Utilization</p>
              <p className="text-2xl font-bold text-primary-black">{utilization.toFixed(1)}%</p>
            </div>
            <div className="w-full mt-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${utilization >= 90 ? 'bg-red-500' : utilization >= 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                  style={{ width: `${Math.min(utilization, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overview'
                  ? 'border-accent-gold text-accent-gold'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'transactions'
                  ? 'border-accent-gold text-accent-gold'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Transactions
            </button>
            {isAdmin && (
              <>
                <button
                  onClick={() => setActiveTab('alerts')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'alerts'
                      ? 'border-accent-gold text-accent-gold'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Alerts {alerts.length > 0 && `(${alerts.length})`}
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'settings'
                      ? 'border-accent-gold text-accent-gold'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Settings
                </button>
              </>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Customer Information */}
              <div>
                <h3 className="text-lg font-semibold text-primary-black mb-4">
                  Customer Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-primary-slate">Full Name</p>
                    <p className="text-primary-black font-medium">{wholesaler.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-primary-slate">Phone</p>
                    <p className="text-primary-black font-medium">{wholesaler.customer_phone}</p>
                  </div>
                  {wholesaler.business_name && (
                    <div>
                      <p className="text-sm text-primary-slate">Business Name</p>
                      <p className="text-primary-black font-medium">{wholesaler.business_name}</p>
                    </div>
                  )}
                  {wholesaler.customer_address && (
                    <div>
                      <p className="text-sm text-primary-slate">Address</p>
                      <p className="text-primary-black font-medium">
                        {wholesaler.customer_address}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Credit Information */}
              <div>
                <h3 className="text-lg font-semibold text-primary-black mb-4">
                  Credit Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-primary-slate">Payment Terms</p>
                    <p className="text-primary-black font-medium">
                      {wholesaler.payment_terms_days} days
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-primary-slate">Upgraded On</p>
                    <p className="text-primary-black font-medium">
                      {formatDate(wholesaler.upgraded_at)}
                    </p>
                  </div>
                  {wholesaler.notes && (
                    <div>
                      <p className="text-sm text-primary-slate">Notes</p>
                      <p className="text-primary-black font-medium">{wholesaler.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div>
              {transactions.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-primary-slate">No transactions yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((txn) => (
                    <div
                      key={txn.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        {getTransactionIcon(txn.transaction_type)}
                        <div>
                          <p className="font-medium text-primary-black capitalize">
                            {txn.transaction_type.replace('_', ' ')}
                          </p>
                          <p className="text-sm text-primary-slate">
                            {formatDate(txn.transaction_date)}
                          </p>
                          {txn.notes && (
                            <p className="text-xs text-primary-slate mt-1">{txn.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p
                          className={`text-lg font-bold ${
                            txn.transaction_type === 'payment' ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {txn.transaction_type === 'payment' ? '+' : '-'}
                          {formatCurrency(Math.abs(txn.amount))}
                        </p>
                        <p className="text-sm text-primary-slate">
                          Balance: {formatCurrency(txn.balance_after)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Alerts Tab */}
          {activeTab === 'alerts' && isAdmin && (
            <div>
              {alerts.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-4" />
                  <p className="text-primary-slate">No active alerts</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert) => (
                    <div
                      key={alert.id}
                      className="flex items-start gap-4 p-4 bg-red-50 border border-red-200 rounded-lg"
                    >
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-1" />
                      <div className="flex-1">
                        <p className="font-medium text-primary-black">{alert.alert_type}</p>
                        <p className="text-sm text-primary-slate mt-1">{alert.message}</p>
                        <p className="text-xs text-primary-slate mt-2">
                          {formatDate(alert.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && isAdmin && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    setCreditLimitForm({ creditLimit: wholesaler.credit_limit, notes: '' });
                    setShowCreditLimitModal(true);
                  }}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Edit className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-primary-black">Update Credit Limit</span>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setStatusForm({ status: wholesaler.status, reason: '' });
                    setShowStatusModal(true);
                  }}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Ban className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-primary-black">Change Status</span>
                  </div>
                </button>

                <button
                  onClick={() => setShowAdjustmentModal(true)}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-primary-black">Balance Adjustment</span>
                  </div>
                </button>

                <button
                  onClick={handleToggleVisibility}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {wholesaler.staff_can_view_balance ? (
                      <Eye className="w-5 h-5 text-gray-600" />
                    ) : (
                      <EyeOff className="w-5 h-5 text-gray-600" />
                    )}
                    <span className="font-medium text-primary-black">
                      {wholesaler.staff_can_view_balance ? 'Hide' : 'Show'} Balance from Staff
                    </span>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setDowngradeForm({ reason: '', targetType: 'retail' });
                    setShowDowngradeModal(true);
                  }}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    <ArrowDownCircle className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-primary-black">Convert / Downgrade</span>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 border-2 border-accent-gold">
            <h2 className="text-xl font-bold text-primary-black mb-4">Record Payment</h2>
            <form onSubmit={handlePayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Amount (₦) *
                </label>
                <input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Payment Method *
                </label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, paymentMethod: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold"
                  required
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="pos">POS</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Payment Reference
                </label>
                <input
                  type="text"
                  value={paymentForm.paymentReference}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, paymentReference: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold"
                  placeholder="Transaction reference"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Notes
                </label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-primary-charcoal text-primary-charcoal rounded-lg hover:bg-secondary-lightGray transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-accent-gold text-primary-black rounded-lg hover:bg-accent-goldDark disabled:opacity-50 transition-colors font-medium"
                >
                  {processing ? 'Processing...' : 'Record Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credit Limit Modal */}
      {showCreditLimitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 border-2 border-accent-gold">
            <h2 className="text-xl font-bold text-primary-black mb-4">Update Credit Limit</h2>
            <form onSubmit={handleCreditLimitUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  New Credit Limit (₦) *
                </label>
                <input
                  type="number"
                  value={creditLimitForm.creditLimit}
                  onChange={(e) =>
                    setCreditLimitForm((prev) => ({ ...prev, creditLimit: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold"
                  min="0"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Notes
                </label>
                <textarea
                  value={creditLimitForm.notes}
                  onChange={(e) =>
                    setCreditLimitForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreditLimitModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-primary-charcoal text-primary-charcoal rounded-lg hover:bg-secondary-lightGray transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-accent-gold text-primary-black rounded-lg hover:bg-accent-goldDark disabled:opacity-50 transition-colors font-medium"
                >
                  {processing ? 'Updating...' : 'Update Limit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adjustment Modal */}
      {showAdjustmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 border-2 border-accent-gold">
            <h2 className="text-xl font-bold text-primary-black mb-4">Balance Adjustment</h2>
            <form onSubmit={handleAdjustment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Amount (₦) *
                </label>
                <input
                  type="number"
                  value={adjustmentForm.amount}
                  onChange={(e) =>
                    setAdjustmentForm((prev) => ({ ...prev, amount: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold"
                  placeholder="Positive to add, negative to deduct"
                  step="0.01"
                  required
                />
                <p className="text-xs text-primary-slate mt-1">
                  Use positive values to increase balance, negative to decrease
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Reason *
                </label>
                <textarea
                  value={adjustmentForm.reason}
                  onChange={(e) =>
                    setAdjustmentForm((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold"
                  rows={3}
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAdjustmentModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-primary-charcoal text-primary-charcoal rounded-lg hover:bg-secondary-lightGray transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-accent-gold text-primary-black rounded-lg hover:bg-accent-goldDark disabled:opacity-50 transition-colors font-medium"
                >
                  {processing ? 'Processing...' : 'Create Adjustment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 border-2 border-accent-gold">
            <h2 className="text-xl font-bold text-primary-black mb-4">Change Status</h2>
            <form onSubmit={handleStatusUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  New Status *
                </label>
                <select
                  value={statusForm.status}
                  onChange={(e) => setStatusForm((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold"
                  required
                >
                  <option value="">Select status...</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Reason *
                </label>
                <textarea
                  value={statusForm.reason}
                  onChange={(e) => setStatusForm((prev) => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold"
                  rows={3}
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowStatusModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-primary-charcoal text-primary-charcoal rounded-lg hover:bg-secondary-lightGray transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-accent-gold text-primary-black rounded-lg hover:bg-accent-goldDark disabled:opacity-50 transition-colors font-medium"
                >
                  {processing ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Downgrade/Convert Modal */}
      {showDowngradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 border-2 border-accent-gold">
            <h2 className="text-xl font-bold text-primary-black mb-4">
              Convert / Downgrade Account
            </h2>

            <div className="mb-4 p-3 bg-yellow-50 text-yellow-800 rounded-lg text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <p>
                This will remove the wholesalers privileges and credit facility. The account must
                have a zero balance and no unresolved alerts.
              </p>
            </div>

            <form onSubmit={handleDowngrade} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Target Customer Type *
                </label>
                <select
                  value={downgradeForm.targetType}
                  onChange={(e) =>
                    setDowngradeForm((prev) => ({ ...prev, targetType: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold"
                  required
                >
                  <option value="retail">Normal Retail Customer</option>
                  <option value="contractor">Contractor</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-charcoal mb-1">
                  Reason *
                </label>
                <textarea
                  value={downgradeForm.reason}
                  onChange={(e) =>
                    setDowngradeForm((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold"
                  rows={3}
                  placeholder="Why is this account being converted?"
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowDowngradeModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-primary-charcoal text-primary-charcoal rounded-lg hover:bg-secondary-lightGray transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-accent-gold text-primary-black rounded-lg hover:bg-accent-goldDark disabled:opacity-50 transition-colors font-medium"
                >
                  {processing ? 'Processing...' : 'Convert Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WholesalerDetailPage;
